import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIngestWrite, useInspection } from '../../hooks/useIssueIngest.js';
import { formatIssueTime } from '../../lib/issue-mgmt-format.js';
import { NO_POINT_REASON_LABEL_KEYS, VoiceRow } from './ExcludedPanel.js';
import { IssueText } from './IssueText.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 月1回の点検（やることの最下段を点検として開いたとき）。先月の除外と先月に取り込んだ声から抜き取った行を出し、
 * 除外の行は「振り分けに戻す」、声の行は「本文を削除」（確認つき）で直す。「点検を終える」で結果を設定タブに残す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 月1回、AI が除外した投稿と取り込んだ声を抜き取りで見直し、本当は声だったものを振り分けに戻し、伏せ字漏れの本文を削除して点検を終える
 * @graph-connects useInspection [calls] 最新の点検
 * @graph-connects useIngestWrite [calls] restoreExcludedVoice・deleteVoiceBody・finishInspection
 * @graph-connects VoiceRow [calls] 1行
 */
export function InspectionPanel({ onClose, now }: { onClose: () => void; now: Date }) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const inspection = useInspection(true);
  const restore = useIngestWrite('restoreExcludedVoice');
  const deleteBody = useIngestWrite('deleteVoiceBody');
  const finish = useIngestWrite('finishInspection');
  const [confirming, setConfirming] = useState<string | null>(null);
  const busy = restore.isPending || deleteBody.isPending || finish.isPending;

  if (inspection.data === undefined) {
    return (
      <p className="mt-6 text-sm text-[var(--color-text-muted)]">
        {inspection.isError ? t('issueMgmt.common.loadError') : t('issueMgmt.common.loading')}
      </p>
    );
  }
  const current = inspection.data.inspection;
  if (current === null) {
    return (
      <section className="mt-6 rounded-xl border border-[var(--color-border)] p-4 text-sm text-[var(--color-text-muted)]">
        {t('issueMgmt.inspection.none')}{' '}
        <Link
          to="/issue-mgmt/settings"
          className="text-[var(--color-accent-primary)] hover:underline"
        >
          {t('issueMgmt.anomaly.openSettings')}
        </Link>
      </section>
    );
  }
  const finished = current.finishedAt !== null;
  const countText = (n: number | null) => (n === null ? t('issueMgmt.common.dash') : String(n));

  return (
    <section className="mt-6 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('issueMgmt.inspection.heading', {
            month: current.targetMonth,
            date: formatIssueTime(current.startedAt, null, now) ?? t('issueMgmt.common.dash'),
          })}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-[var(--color-accent-primary)] hover:underline"
        >
          {t('issueMgmt.excluded.close')}
        </button>
      </div>
      <p className="text-xs text-[var(--color-text-muted)]">{t('issueMgmt.inspection.help')}</p>
      <ul className="divide-y divide-[var(--color-border)] rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        {current.rows.map((row) => {
          const action = (() => {
            if (finished) return null;
            if (row.kind === 'excluded') {
              return row.restored ? (
                <span className="text-xs text-[var(--color-success)]">
                  {t('issueMgmt.inspection.restored')}
                </span>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    restore.mutate(
                      { voiceId: row.voiceId },
                      {
                        onSuccess: () => {
                          toast.notifySaved(t('issueMgmt.excluded.restored'), {
                            detail: t('issueMgmt.excluded.restoredDetail'),
                          });
                        },
                        onError: toast.notifyError,
                      },
                    );
                  }}
                >
                  {t('issueMgmt.excluded.restore')}
                </Button>
              );
            }
            if (row.bodyDeleted) {
              return (
                <span className="text-xs text-[var(--color-text-muted)]">
                  {t('issueMgmt.voices.deleted')}
                </span>
              );
            }
            if (confirming === row.voiceId) {
              return (
                <span className="flex items-center gap-2">
                  <span className="text-xs text-[var(--color-error)]">
                    {t('issueMgmt.inspection.deleteConfirm')}
                  </span>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      deleteBody.mutate(
                        { voiceId: row.voiceId },
                        {
                          onSuccess: () => {
                            setConfirming(null);
                            toast.notifySaved(t('issueMgmt.inspection.deleted'));
                          },
                          onError: toast.notifyError,
                        },
                      );
                    }}
                  >
                    {t('issueMgmt.inspection.deleteYes')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setConfirming(null);
                    }}
                  >
                    {t('issueMgmt.common.cancel')}
                  </Button>
                </span>
              );
            }
            return (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setConfirming(row.voiceId);
                }}
              >
                {t('issueMgmt.inspection.delete')}
              </Button>
            );
          })();
          return (
            <VoiceRow
              key={`${row.kind}-${row.voiceId}`}
              postedAt={row.postedAt}
              postedDate={null}
              channelName={row.channelName}
              isPrivate={row.isPrivate}
              text={
                row.text === null ? (
                  <span className="text-[var(--color-text-muted)]">
                    {t('issueMgmt.voices.deleted')}
                  </span>
                ) : (
                  <IssueText text={row.text} />
                )
              }
              slackUrl={row.slackUrl}
              badge={
                <Badge>
                  {row.kind === 'voice'
                    ? t('issueMgmt.inspection.voiceRow')
                    : row.excludedReason === null
                      ? t('issueMgmt.excluded.reason.none')
                      : t(NO_POINT_REASON_LABEL_KEYS[row.excludedReason])}
                </Badge>
              }
              action={action}
              now={now}
            />
          );
        })}
      </ul>
      {finished ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t('issueMgmt.inspection.result', {
            real: countText(current.realVoiceCount),
            leak: countText(current.leakCount),
          })}
        </p>
      ) : (
        <Button
          loading={finish.isPending}
          disabled={busy}
          onClick={() => {
            finish.mutate(
              { inspectionId: current.inspectionId },
              {
                onSuccess: (result) => {
                  toast.notifySaved(t('issueMgmt.inspection.finished'), {
                    detail: t('issueMgmt.inspection.result', {
                      real: countText(result.realVoiceCount),
                      leak: countText(result.leakCount),
                    }),
                  });
                  onClose();
                },
                onError: toast.notifyError,
              },
            );
          }}
        >
          {t('issueMgmt.inspection.finish')}
        </Button>
      )}
    </section>
  );
}
